import React from "react";
import { CustomBlocksProvider, CustomBlocksProviderState } from "./custom-blocks-provider";
import { AiActionProvider, AiActionProviderState } from "./provider-partitions/ai-action-provider";
import { ApiManagementProvider, ApiManagementProviderState } from "./provider-partitions/api-management-provider";
import { BluetoothMethodProvider, BluetoothMethodProviderState } from "./provider-partitions/bluetooth-method-provider";
import { BranchingProvider, BranchingProviderState } from "./provider-partitions/branching-provider";
import { CollabProvider, CollabProviderState } from "./provider-partitions/collab-provider";
import { ConfigManagerProvider, ConfigManagerProviderState } from "./provider-partitions/config-manager-provider";
import { DatasourceProvider, DatasourceProviderState } from "./provider-partitions/datasource-provider";
import { DefaultStyleProvider, DefaultStyleProviderState } from "./provider-partitions/default-style-provider";
import { DesignSituationProvider, DesignSituationProviderState } from "./provider-partitions/design-situation-provider";
import {
  DesignTimeElementStateProvider,
  DesignTimeElementStateProviderState
} from "./provider-partitions/design-time-element-state-provider";
import { DisplayItemProvider, DisplayItemProviderState } from "./provider-partitions/display-item-provider";
import {
  DocumentParametersProvider,
  DocumentParametersProviderState
} from "./provider-partitions/document-parameters-provider";
import { DocumentsProvider, DocumentsProviderState } from "./provider-partitions/documents-provider";
import { DynamicTypesProvider, DynamicTypesProviderState } from "./provider-partitions/dynamic-types-provider";
import { ExceptionHandleProvider, ExceptionHandleProviderState } from "./provider-partitions/exception-handle-provider";
import { FolderProvider, FolderProviderState } from "./provider-partitions/folder-provider";
import { FrameProvider, FrameProviderState } from "./provider-partitions/frame-provider";
import { HelpdeskProvider, HelpdeskProviderState } from "./provider-partitions/helpdesk-provider";
import { InvisibleStylesProvider, InvisibleStylesProviderState } from "./provider-partitions/invisible-styles-provider";
import { NavigateProvider, NavigateProviderState } from "./provider-partitions/navigate-provider";
import {
  NotificationTemplateProvider,
  NotificationTemplateProviderState
} from "./provider-partitions/notification-template-provider";
import { ProjectColorsProvider, ProjectColorsProviderState } from "./provider-partitions/project-colors-provider";
import { ProjectProvider, ProjectProviderState } from "./provider-partitions/project-provider";
import { PropertyProvider, PropertyProviderState } from "./provider-partitions/property-provider";
import { QueryBuilderProvider, QueryBuilderProviderState } from "./provider-partitions/query-builder-provider";
import { ReportDesignProvider, ReportDesignProviderState } from "./provider-partitions/report-design-provider";
import { ResponsiveProvider, ResponsiveProviderState } from "./provider-partitions/responsive-provider";
import { ScheduledJobsProvider, ScheduledJobsProviderState } from "./provider-partitions/scheduled-jobs-provider";
import {
  ScreenAuthorizationProvider,
  ScreenAuthorizationProviderState
} from "./provider-partitions/screen-authorization-provider";
import { ScreenProvider, ScreenProviderState } from "./provider-partitions/screen-provider";
import { WizardProvider, WizardProviderState } from "./provider-partitions/wizard-provider";
import { WorkflowCollabProvider, WorkflowCollabProviderState } from "./provider-partitions/workflow-collab-provider";
import { WorkflowProvider, WorkflowProviderState } from "./provider-partitions/workflow-provider";
import { ZendeskProvider, ZendeskProviderState } from "./provider-partitions/zendesk-provider";

export type IScreenDesignContextState = DesignSituationProviderState &
  ProjectProviderState &
  ScreenProviderState &
  DisplayItemProviderState &
  PropertyProviderState &
  ResponsiveProviderState &
  DocumentsProviderState &
  NavigateProviderState &
  ExceptionHandleProviderState &
  ScreenAuthorizationProviderState &
  WorkflowProviderState &
  ScheduledJobsProviderState &
  DocumentParametersProviderState &
  ApiManagementProviderState &
  FrameProviderState &
  ZendeskProviderState &
  ReportDesignProviderState &
  WizardProviderState &
  ConfigManagerProviderState &
  BranchingProviderState &
  InvisibleStylesProviderState &
  DefaultStyleProviderState &
  DynamicTypesProviderState &
  FolderProviderState &
  DatasourceProviderState &
  QueryBuilderProviderState &
  NotificationTemplateProviderState &
  BluetoothMethodProviderState &
  ProjectColorsProviderState &
  CollabProviderState &
  WorkflowCollabProviderState &
  HelpdeskProviderState &
  AiActionProviderState &
  CustomBlocksProviderState &
  DesignTimeElementStateProviderState;

export const DefaultScreenDesignContextState: IScreenDesignContextState = {
  ...DesignSituationProvider.defaultState,
  ...ProjectProvider.defaultState,
  ...ScreenProvider.defaultState,
  ...DisplayItemProvider.defaultState,
  ...PropertyProvider.defaultState,
  ...ResponsiveProvider.defaultState,
  ...DocumentsProvider.defaultState,
  ...NavigateProvider.defaultState,
  ...ExceptionHandleProvider.defaultState,
  ...ScreenAuthorizationProvider.defaultState,
  ...WorkflowProvider.defaultState,
  ...ScheduledJobsProvider.defaultState,
  ...DocumentParametersProvider.defaultState,
  ...ApiManagementProvider.defaultState,
  ...FrameProvider.defaultState,
  ...ReportDesignProvider.defaultState,
  ...ZendeskProvider.defaultState,
  ...WizardProvider.defaultState,
  ...ConfigManagerProvider.defaultState,
  ...BranchingProvider.defaultState,
  ...InvisibleStylesProvider.defaultState,
  ...DefaultStyleProvider.defaultState,
  ...DynamicTypesProvider.defaultState,
  ...FolderProvider.defaultState,
  ...DatasourceProvider.defaultState,
  ...QueryBuilderProvider.defaultState,
  ...NotificationTemplateProvider.defaultState,
  ...BluetoothMethodProvider.defaultState,
  ...ProjectColorsProvider.defaultState,
  ...CollabProvider.defaultState,
  ...WorkflowCollabProvider.defaultState,
  ...HelpdeskProvider.defaultState,
  ...AiActionProvider.defaultState,
  ...CustomBlocksProvider.defaultState,
  ...DesignTimeElementStateProvider.defaultState
};

export const ScreenDesignContext = React.createContext<IScreenDesignContextState>(DefaultScreenDesignContextState);

ScreenDesignContext.displayName = "ScreenDesignContext";

export interface ScreenDesignContextConsumerProps {
  ScreenDesignContext: IScreenDesignContextState;
}
